<template>
  <a-layout class="app-shell">
    <a-layout-header class="app-header">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true"><ApiOutlined /></span>
        <div>
          <strong>热血江湖节点代理</strong>
          <span>服务器与节点运营控制台</span>
        </div>
      </div>
      <div class="mobile-page-title">
        <span class="mobile-status-dot" aria-hidden="true" />
        <div>
          <small>移动控制台</small>
          <strong>{{ currentPageTitle }}</strong>
        </div>
      </div>
      <div class="header-actions">
        <NotificationBell />
        <a-dropdown>
          <a-button type="text" class="account-button">
            <UserOutlined />
            <span>admin</span>
          </a-button>
          <template #overlay>
            <a-menu>
              <a-menu-item @click="onLogout">退出</a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
    </a-layout-header>

    <a-layout class="app-body">
      <a-layout-sider width="208" class="desktop-sider">
        <a-menu mode="inline" :selected-keys="[$route.path]" @click="onMenu">
          <a-menu-item key="/"><HomeOutlined />首页</a-menu-item>
          <a-menu-item key="/nodes"><ClusterOutlined />服务器</a-menu-item>
          <a-menu-item key="/health"><HeartOutlined />健康总览</a-menu-item>
          <a-menu-item key="/proxies"><SwapOutlined />节点</a-menu-item>
          <a-menu-item key="/proxy-users"><TeamOutlined />用户</a-menu-item>
          <a-menu-item key="/leaderboard"><TrophyOutlined />排行榜</a-menu-item>
          <a-menu-item key="/topology"><ApartmentOutlined />拓扑</a-menu-item>
          <a-menu-item key="/region-lines"><GlobalOutlined />大区</a-menu-item>
          <a-menu-item key="/notifications"><BellOutlined />通知中心</a-menu-item>
          <a-menu-item key="/task-center"><ClockCircleOutlined />定时任务</a-menu-item>
          <a-menu-item key="/traffic-usage"><BarChartOutlined />流量统计</a-menu-item>
          <a-menu-item key="/audit-logs"><FileSearchOutlined />审计日志</a-menu-item>
          <a-menu-item key="/settings"><SettingOutlined />设置</a-menu-item>
        </a-menu>
      </a-layout-sider>

      <a-layout-content class="app-content">
        <router-view />
      </a-layout-content>
    </a-layout>

    <nav
      class="mobile-primary-nav"
      data-testid="mobile-primary-nav"
      aria-label="移动端主导航"
    >
      <button
        type="button"
        :class="{ active: $route.path === '/' }"
        @click="onMobileNav('/')"
      >
        <HomeOutlined />
        <span>首页</span>
      </button>
      <button
        type="button"
        :class="{ active: $route.path === '/nodes' }"
        @click="onMobileNav('/nodes')"
      >
        <ClusterOutlined />
        <span>服务器</span>
      </button>
      <button
        type="button"
        class="mobile-create-button"
        data-testid="mobile-open-create"
        aria-label="打开创建菜单"
        @click="quickCreateVisible = true"
      >
        <span class="mobile-create-orb"><PlusOutlined /></span>
        <span>创建</span>
      </button>
      <button
        type="button"
        :class="{ active: $route.path === '/proxies' }"
        @click="onMobileNav('/proxies')"
      >
        <SwapOutlined />
        <span>节点</span>
      </button>
      <button
        type="button"
        :class="{ active: moreOpen }"
        @click="moreOpen = true"
      >
        <AppstoreOutlined />
        <span>更多</span>
      </button>
    </nav>

    <div
      v-if="quickCreateVisible"
      class="mobile-sheet-backdrop"
      @click.self="quickCreateVisible = false"
    >
      <section class="mobile-sheet" role="dialog" aria-modal="true" aria-label="快捷创建">
        <div class="mobile-sheet-handle" />
        <div class="mobile-sheet-heading">
          <div>
            <small>快捷操作</small>
            <h2>要创建什么？</h2>
          </div>
          <button type="button" class="sheet-close" @click="quickCreateVisible = false">关闭</button>
        </div>
        <div class="quick-action-grid">
          <button
            type="button"
            data-testid="mobile-create-node"
            @click="openCreate('/nodes')"
          >
            <span class="quick-action-icon quick-action-icon--node"><ClusterOutlined /></span>
            <strong>创建服务器</strong>
            <small>生成新的 Agent 安装命令</small>
          </button>
          <button
            type="button"
            data-testid="mobile-create-proxy"
            @click="openCreate('/proxies')"
          >
            <span class="quick-action-icon quick-action-icon--proxy"><SwapOutlined /></span>
            <strong>创建节点</strong>
            <small>选择用户、大区和目标服务器</small>
          </button>
        </div>
      </section>
    </div>

    <div
      v-if="moreOpen"
      class="mobile-sheet-backdrop"
      @click.self="moreOpen = false"
    >
      <section class="mobile-sheet" role="dialog" aria-modal="true" aria-label="更多功能">
        <div class="mobile-sheet-handle" />
        <div class="mobile-sheet-heading">
          <div>
            <small>全部功能</small>
            <h2>管理工具</h2>
          </div>
          <button type="button" class="sheet-close" @click="moreOpen = false">关闭</button>
        </div>
        <div class="more-grid">
          <button v-for="item in moreItems" :key="item.path" type="button" @click="onMobileNav(item.path)">
            <component :is="item.icon" />
            <span>{{ item.label }}</span>
          </button>
          <button type="button" class="logout-tile" @click="onLogout">
            <LogoutOutlined />
            <span>退出登录</span>
          </button>
        </div>
      </section>
    </div>
  </a-layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { logout } from '../api/auth';
import { message } from 'ant-design-vue';
import NotificationBell from '../components/NotificationBell.vue';
import {
  ApiOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
  BellOutlined,
  BarChartOutlined,
  ClusterOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  FileSearchOutlined,
  HeartOutlined,
  HomeOutlined,
  LogoutOutlined,
  PlusOutlined,
  SettingOutlined,
  SwapOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons-vue';

const router = useRouter();
const route = useRoute();
const quickCreateVisible = ref(false);
const moreOpen = ref(false);

const pageTitles: Record<string, string> = {
  '/': '数据总览',
  '/nodes': '服务器',
  '/health': '健康总览',
  '/proxies': '节点',
  '/proxy-users': '用户',
  '/leaderboard': '排行榜',
  '/topology': '拓扑',
  '/region-lines': '大区',
  '/regions': '大区',
  '/settings': '设置',
  '/notifications': '通知中心',
  '/task-center': '定时任务中心',
  '/traffic-usage': '用户流量统计',
  '/audit-logs': '审计日志',
};

const currentPageTitle = computed(() => pageTitles[route.path] || '控制台');
const moreItems = [
  { path: '/health', label: '健康', icon: HeartOutlined },
  { path: '/proxy-users', label: '用户', icon: TeamOutlined },
  { path: '/leaderboard', label: '排行榜', icon: TrophyOutlined },
  { path: '/topology', label: '拓扑', icon: ApartmentOutlined },
  { path: '/region-lines', label: '大区', icon: GlobalOutlined },
  { path: '/notifications', label: '通知', icon: BellOutlined },
  { path: '/task-center', label: '定时任务', icon: ClockCircleOutlined },
  { path: '/traffic-usage', label: '流量统计', icon: BarChartOutlined },
  { path: '/audit-logs', label: '审计日志', icon: FileSearchOutlined },
  { path: '/settings', label: '设置', icon: SettingOutlined },
];

const onMenu = ({ key }: { key: string }) => router.push(key);
const onMobileNav = (path: string) => {
  moreOpen.value = false;
  quickCreateVisible.value = false;
  router.push(path);
};
const openCreate = (path: '/nodes' | '/proxies') => {
  quickCreateVisible.value = false;
  router.push({ path, query: { create: '1' } });
};
const onLogout = async () => {
  await logout();
  message.success('已退出');
  router.push('/login');
};
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  background: #f3f6fa;
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  line-height: normal;
  padding: 0 24px;
  border-bottom: 1px solid #e4e9f0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 11px;
}

.brand-mark {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 10px;
  background: #0f2942;
  color: #5eead4;
  font-size: 18px;
}

.brand strong,
.brand span {
  display: block;
}

.brand strong {
  color: #132238;
  font-size: 15px;
  line-height: 1.35;
}

.brand div > span {
  margin-top: 1px;
  color: #8793a3;
  font-size: 10px;
  letter-spacing: 0.12em;
}

.mobile-page-title {
  display: none;
}

.account-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #344256;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.app-body {
  min-width: 0;
}

.desktop-sider {
  position: sticky;
  top: 64px;
  height: calc(100vh - 64px);
  overflow-y: auto;
  border-right: 1px solid #e4e9f0;
  background: #fff !important;
}

.desktop-sider :deep(.ant-menu) {
  padding-top: 12px;
  border-inline-end: 0;
}

.app-content {
  min-width: 0;
  padding: 24px;
  background: #f3f6fa;
}

.mobile-primary-nav,
.mobile-sheet-backdrop {
  display: none;
}

@media (max-width: 767px) {
  .app-header {
    height: 56px;
    padding: 0 14px;
    border-bottom-color: #1f3e5d;
    background: #10283f;
  }

  .brand {
    display: none;
  }

  .mobile-page-title {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #fff;
  }

  .mobile-page-title small,
  .mobile-page-title strong {
    display: block;
  }

  .mobile-page-title small {
    color: #93a9bd;
    font-size: 9px;
    letter-spacing: 0.13em;
  }

  .mobile-page-title strong {
    margin-top: 1px;
    font-size: 15px;
    line-height: 1.2;
  }

  .mobile-status-dot {
    width: 8px;
    height: 8px;
    border: 2px solid #10283f;
    border-radius: 999px;
    background: #45d6b5;
    box-shadow: 0 0 0 3px rgba(69, 214, 181, 0.17);
  }

  .account-button {
    height: 36px;
    padding: 0 8px;
    color: #d8e4ee;
  }

  .account-button span {
    display: none;
  }

  .desktop-sider {
    display: none;
  }

  .app-content {
    width: 100%;
    padding: 12px 12px calc(88px + env(safe-area-inset-bottom));
    overflow-x: hidden;
  }

  .mobile-primary-nav {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 50;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    min-height: 66px;
    padding: 6px 6px calc(5px + env(safe-area-inset-bottom));
    border-top: 1px solid #dce3eb;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 -8px 28px rgba(15, 41, 66, 0.09);
    backdrop-filter: blur(16px);
  }

  .mobile-primary-nav button {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 48px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 3px;
    border: 0;
    background: transparent;
    color: #7d8998;
    font-size: 10px;
    line-height: 1;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-primary-nav button :deep(.anticon) {
    font-size: 19px;
  }

  .mobile-primary-nav button.active {
    color: #0b68cb;
  }

  .mobile-create-button {
    justify-content: flex-end !important;
    color: #18324c !important;
  }

  .mobile-create-orb {
    display: grid;
    width: 44px;
    height: 44px;
    margin-top: -24px;
    place-items: center;
    border: 4px solid #f3f6fa;
    border-radius: 50%;
    background: #0b68cb;
    box-shadow: 0 7px 18px rgba(11, 104, 203, 0.3);
    color: #fff;
  }

  .mobile-create-orb :deep(.anticon) {
    font-size: 20px;
  }

  .mobile-sheet-backdrop {
    position: fixed;
    inset: 0;
    z-index: 80;
    display: flex;
    align-items: flex-end;
    background: rgba(8, 24, 39, 0.46);
    backdrop-filter: blur(3px);
  }

  .mobile-sheet {
    width: 100%;
    padding: 8px 16px calc(22px + env(safe-area-inset-bottom));
    border-radius: 20px 20px 0 0;
    background: #fff;
    box-shadow: 0 -18px 45px rgba(8, 24, 39, 0.18);
  }

  .mobile-sheet-handle {
    width: 38px;
    height: 4px;
    margin: 0 auto 14px;
    border-radius: 999px;
    background: #d9e0e8;
  }

  .mobile-sheet-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .mobile-sheet-heading small {
    color: #78879a;
    font-size: 10px;
    letter-spacing: 0.12em;
  }

  .mobile-sheet-heading h2 {
    margin: 2px 0 0;
    color: #12263a;
    font-size: 21px;
  }

  .sheet-close {
    min-height: 36px;
    padding: 0 10px;
    border: 0;
    background: transparent;
    color: #627084;
  }

  .quick-action-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .quick-action-grid > button {
    display: flex;
    min-height: 142px;
    align-items: flex-start;
    padding: 16px;
    border: 1px solid #e1e7ee;
    border-radius: 14px;
    background: #f8fafc;
    text-align: left;
    flex-direction: column;
  }

  .quick-action-icon {
    display: grid;
    width: 38px;
    height: 38px;
    margin-bottom: 16px;
    place-items: center;
    border-radius: 10px;
    font-size: 19px;
  }

  .quick-action-icon--node {
    background: #e9f8f4;
    color: #0a846c;
  }

  .quick-action-icon--proxy {
    background: #e9f2fc;
    color: #0b68cb;
  }

  .quick-action-grid strong {
    color: #132238;
    font-size: 15px;
  }

  .quick-action-grid small {
    margin-top: 5px;
    color: #78879a;
    font-size: 11px;
    line-height: 1.45;
  }

  .more-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .more-grid button {
    display: flex;
    min-height: 78px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid #e2e8ef;
    border-radius: 12px;
    background: #f8fafc;
    color: #304258;
    flex-direction: column;
  }

  .more-grid button :deep(.anticon) {
    color: #0b68cb;
    font-size: 19px;
  }

  .more-grid .logout-tile {
    color: #b42318;
  }

  .more-grid .logout-tile :deep(.anticon) {
    color: #b42318;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mobile-primary-nav *,
  .mobile-sheet-backdrop * {
    transition: none !important;
  }
}
</style>
